import type {
    Address,
    CartContactInfo,
    Employee,
    Sku,
    ShippingService,
    ShipmentEstimate,
    GenericObject
} from './';

export enum SalesTransactionType {
    ORDER = 'order',
    INVOICE = 'invoice',
    CREDIT_MEMO = 'credit_memo'
}

export enum SalesTransactionCreditMemoStatus {}

export enum SalesTransactionInvoiceStatus {
    OPEN = 'open',
    PAST_DUE = 'past_due',
    PENDING_BANK_CONFIRMATION = 'pending_bank_confirmation',
    PAID = 'paid'
}

export enum SalesTransactionOrderStatusGroup {
    DRAFT = 'draft',
    ACTIVE = 'active',
    FULFILLED = 'fulfilled',
    CANCELED = 'canceled'
}

export enum SalesTransactionOrderStatus {
    DRAFT = 'draft',
    QUOTE = 'quote',
    ACTION_NEEDED = 'action_needed',
    WAITING = 'waiting',
    PREPARING = 'preparing',
    LABELED = 'labeled',
    FULFILLED = 'fulfilled',
    CANCELED = 'canceled'
}

export enum SalesTransactionOrderLineStatus {
    SCHEDULED = 'scheduled',
    PREORDER = 'preorder',
    BACKORDER = 'backorder',
    PENDING_PRODUCTION = 'pending_production',
    IN_PRODUCTION = 'in_production',
    PENDING_ASSEMBLY = 'pending_assembly',
    IN_ASSEMBLY = 'in_assembly',
    AVAILABLE = 'available',
    FULFILLED = 'fulfilled',
    SOLD_OUT = 'sold_out',
    CANCELED = 'canceled',
    UNKNOWN = 'unknown'
}

/**
*  @deprecated This MAY not be needed anymore
*/
export enum SalesTransactionOrderInternalStatus {
    DRAFT = 'draft',
    PENDING = 'pending',
    OPEN = 'open',
    RELEASED = 'released',
    POSTED = 'posted',
    COMPLETED = 'completed',
    CANCELED = 'canceled'
}

export type SalesTransactionStatus =
    typeof SalesTransactionInvoiceStatus |
    typeof SalesTransactionOrderStatus |
    typeof SalesTransactionOrderLineStatus |
    typeof SalesTransactionOrderInternalStatus |
    typeof SalesTransactionCreditMemoStatus;

export enum SalesTransactionUidPrefix {
    ORDER = 'so',
    INVOICE = 'si',
    CREDIT_MEMO = 'scm'
}

export enum SalesTransactionShippingServicePreference {
    BEST_VALUE = 'best_value',
    CHEAPEST = 'cheapest',
    CHEAPEST_PLUS = 'cheapest_plus',
    FASTEST = 'fastest',
    FASTEST_PLUS = 'fastest_plus'
}

export enum SalesTransactionCustomerFlag {
    UNSIGNED_AGREEMENT = 'unsigned_agreement',
    PAYMENT_REQUIRED = 'payment_required',
    BACKORDER_INVENTORY = 'backorder_inventory',
    PREORDER_INVENTORY = 'preorder_inventory',
    SCHEDULED_DATE = 'scheduled_date',
    BELOW_THRESHOLD = 'below_threshold',
    ASSEMBLING_INVENTORY = 'assembling_inventory',
    PACKING_ORDER = 'packing_order'
}

export type SalesTransactionCustomerFlagWithDescription = GenericObject<string, SalesTransactionCustomerFlag>;

export interface SalesTransactionTypeObj {
    type: SalesTransactionType;
    title: string;
    titlePlural: string;
    itemDetailPath: string;
    permissionProp: string;
}

export interface SalesTransactionCustomerInfo {
    customerNotes: string;
}

export interface SalesTransactionGenericValueItem {
    value: string;
    url?: string;
}

export interface SalesTransactionGenericContentItem {
    label: string;
}

export interface SalesTransactionSummary extends SalesTransactionGenericContentItem, SalesTransactionGenericValueItem {}

export interface SalesTransactionTotal extends SalesTransactionGenericContentItem, SalesTransactionGenericValueItem {}

export interface SalesTransactionDetail extends SalesTransactionGenericContentItem {
    lines: SalesTransactionGenericValueItem[];
}


// Order Only
export interface SalesOrderTransactionLineExtension {
    status: Nullable<SalesTransactionOrderLineStatus>;
    statusDescription: string;
    /**
    *  @deprecated DO NOT USE: use `availableDate`
    */
    date: Nullable<string>;
    availableDate?: Nullable<string>;
}
export interface SalesOrderTransactionExtension {
    isEditable: boolean;
}

export interface SalesOrderTransactionExpandable {
    lines?: Nullable<SalesTransactionLine[]>;
    details?: Nullable<SalesTransactionDetail[]>;
    summary?: Nullable<SalesTransactionSummary[]>;
    billToAddress?: Nullable<Address>;
    shipToAddress?: Nullable<Address>;
    pickupAddress?: Nullable<Address>;
    shippingService?: Nullable<ShippingService>;
    shippingServicePreference?: Nullable<SalesTransactionShippingServicePreference>;
    canRequestReturnLabel?: boolean;
}


export interface SalesTransactionLine extends Partial<SalesOrderTransactionLineExtension> {
    qty: number;
    unitPrice: number;
    discountedUnitPrice: Nullable<number>;
    subtotal: number;

    itemNo: string;
    referenceNo: string;
    upc: string;
    discountPct: string;

    discount: number;
    total: number;
    type: string;
    name: string;
    detail: Nilish<string>;
    imageUrl: Nilish<string>;
    salesOrderLineUid: Nilish<string>;

    // TODO: This is a guess. Need to confirm.
    bundle: Nilish<string>;

    // Expanded fields
    sku?: Nullable<Sku>;
}

export interface SalesTransactionBase {
    uid: string;
    documentNo: string;
    date: string;

    /**
    *  @deprecated Use customerStatus
    */
    status: SalesTransactionStatus;
    internalStatus: SalesTransactionOrderInternalStatus;

    customerStatus: SalesTransactionStatus;
    customerStatusDescription: string;
    customerFlags?: SalesTransactionCustomerFlag[];
    customerFlagsWithDescriptions?: SalesTransactionCustomerFlagWithDescription;

    shipDate: string;
    requestedShipDate: Nullable<string>;
    requestedDeliveryDate: Nullable<string>;
    orderDate: string;
    dueDate: string;
    orderNo: string;
    purchaseOrderNo: Nilish<string>;
    paymentTerms: string;
    accountCreditApplied: number;
    subtotal: number;
    discountsTotal: number;
    shippingTotal: number;
    taxTotal: number;
    total: number;
    balance: number;
    isOpen: boolean;
    isBooking: boolean;
    downloads?: Nilish<SalesTransactionGenericValueItem[]>;
    totals: SalesTransactionTotal[];
    backorderPreference?: Nilish<string>;
    source?: Nilish<string>;
    estimate?: Nilish<ShipmentEstimate>;
    shippingAccount?: Nilish<string>;
    confirmed?: Nilish<string>;
    created: string;
    updated: string;
}

export interface SalesTransaction extends
    SalesTransactionBase,
    CartContactInfo,
    SalesTransactionCustomerInfo,
    SalesOrderTransactionExpandable,
    Partial<SalesOrderTransactionExtension> {
        isDropship: boolean;
        isLocalPickup: boolean;
        isPaymentRequired: boolean;
        isTest: boolean;
    }


// Sales Person

export interface Salesperson {
    name: Nullable<string>;
    title: Nullable<string>;
    email: Nullable<string>;
    phone: Nullable<string>;
    formattedPhone: Nullable<string>;
    /**
    *  @deprecated
    */
    employee: Employee;
}


// Discounts
export interface DiscountsSummaryBase {
    uid: string;
    name: string;
    discount: string;
    discountFmt: string;
}

export interface DiscountsSummarySku extends DiscountsSummaryBase {}

export interface DiscountsSummaryProduct extends DiscountsSummaryBase {
    skus: DiscountsSummarySku[];
}

export interface DiscountsSummaryCategory extends DiscountsSummaryBase {
    products: DiscountsSummaryProduct[];
}

export interface DiscountsSummaryMarket extends DiscountsSummaryBase {
    categories: DiscountsSummaryCategory[];
}

export type DiscountsSummary = {
    markets: DiscountsSummaryMarket[];
};
